import React, { useEffect, useState } from 'react';
import { Loader2, Save, Receipt } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PlzOrtBridge } from '@/components/address/PlzOrtBridge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const INTERVALS = [
  { value: 'manual', label: 'Manuell (kein automatischer Versand)' },
  { value: 'monthly', label: 'Monatlich' },
  { value: 'quarterly', label: 'Vierteljährlich (1/4-jährlich)' },
  { value: 'semiannual', label: 'Halbjährlich (1/2-jährlich)' },
  { value: 'yearly', label: 'Jährlich' },
];

const FIELDS = [
  { section: 'Anschrift & Kontakt (Rechnungskopf/-fuß)', items: [
    { key: 'street', label: 'Straße & Hausnummer' },
    { key: 'postal_code', label: 'PLZ' },
    { key: 'city', label: 'Ort' },
    { key: 'phone', label: 'Telefon' },
    { key: 'email', label: 'E-Mail' },
  ]},
  { section: 'Steuerliche Angaben (§ 14 UStG)', items: [
    { key: 'tax_number', label: 'Steuernummer' },
    { key: 'vat_id', label: 'USt-IdNr. (optional)' },
  ]},
  { section: 'Bankverbindung', items: [
    { key: 'account_holder', label: 'Kontoinhaber' },
    { key: 'bank_name', label: 'Bank' },
    { key: 'iban', label: 'IBAN' },
    { key: 'bic', label: 'BIC' },
  ]},
];

export const InvoiceSettingsDialog = ({ tenant, onClose }) => {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setLoading(true);
    api.get(`/tenants/${tenant.id}/invoice-settings`)
      .then(r => setValues(r.data || {}))
      .catch(e => toast.error(e.response?.data?.detail || 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }, [tenant]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenant.id}/invoice-settings`, values);
      toast.success('Rechnungsangaben gespeichert');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const set = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  return (
    <Dialog open={!!tenant} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-brand-red" /> Rechnungsangaben – {tenant?.name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /> Lade…</div>
        ) : (
          <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
            {FIELDS.map(sec => (
              <section key={sec.section}>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">{sec.section}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {sec.items.map(f => (
                    <div key={f.key}>
                      <Label className="text-xs">{f.label}</Label>
                      <Input value={values[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}
                             data-testid={`invoice-settings-${f.key}`} className="h-9" />
                      {f.key === 'city' && (
                        <PlzOrtBridge
                          postalCode={values.postal_code}
                          city={values.city}
                          onCity={(value) => set('city', value)}
                          testId="invoice-plz"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Automatischer Rechnungsversand</h3>
              <Label className="text-xs">Turnus</Label>
              <Select value={values.dispatch_interval || 'manual'}
                      onValueChange={(v) => set('dispatch_interval', v)}>
                <SelectTrigger className="h-9" data-testid="invoice-settings-dispatch-interval">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTERVALS.map(i => (
                    <SelectItem key={i.value} value={i.value} data-testid={`dispatch-interval-${i.value}`}>{i.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Zu Beginn jeder Periode werden alle offenen, noch nicht versendeten Rechnungen automatisch
                per E-Mail (PDF-Anhang) an die Familien/Zahler gesendet.
                {values.last_auto_dispatch && <> Letzter automatischer Versand: {values.last_auto_dispatch}</>}
              </p>
            </section>
            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Hinweise auf der Rechnung</h3>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Steuerhinweis <span className="text-slate-400 font-normal">(Standard: „Umsatzsteuerfreie Leistungen gemäß § 4 Nr. 21 UStG.")</span></Label>
                  <Textarea rows={2} value={values.tax_note || ''} onChange={(e) => set('tax_note', e.target.value)}
                            placeholder="Umsatzsteuerfreie Leistungen gemäß § 4 Nr. 21 UStG."
                            data-testid="invoice-settings-tax_note" />
                </div>
                <div>
                  <Label className="text-xs">Zahlungshinweis <span className="text-slate-400 font-normal">(leer = automatischer Text mit Fälligkeit + Rechnungsnummer, sofern IBAN hinterlegt)</span></Label>
                  <Textarea rows={2} value={values.payment_note || ''} onChange={(e) => set('payment_note', e.target.value)}
                            data-testid="invoice-settings-payment_note" />
                </div>
              </div>
            </section>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Abbrechen</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-brand-red hover:bg-brand-red/90 text-white" data-testid="invoice-settings-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceSettingsDialog;
